import React, { useState } from 'react';
import { Card, Form, Input, Button, Alert, Typography, Radio, DatePicker, TimePicker, Select, Switch, Modal } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined, SmileOutlined, ProjectTwoTone, SafetyCertificateTwoTone, CloseCircleOutlined } from '@ant-design/icons';


const { Title, Paragraph } = Typography;
const { Option } = Select;

interface AuthPageProps {
  onAuthSuccess: (token: string, email: string, isNewRegistration?: boolean) => void;
  API_URL: string;
}

export const AuthPage: React.FC<AuthPageProps> = ({ onAuthSuccess, API_URL }) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [unknownTob, setUnknownTob] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [disclaimerModalOpen, setDisclaimerModalOpen] = useState(false);
  const [form] = Form.useForm();

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const url = mode === 'login' 
        ? `${API_URL}/api/v1/personal/auth/login` 
        : `${API_URL}/api/v1/personal/auth/register`;

      let payload: any = {
        email: values.email,
        password: values.password
      };

      if (mode === 'register') {
        if (!values.name || !values.gender || !values.dob || !values.timezone) {
          throw new Error('Semua data profil wajib diisi untuk pendaftaran.');
        }
        const dobStr = values.dob ? values.dob.format('YYYY-MM-DD') : '';
        const tobStr = unknownTob ? '00:00' : (values.tob ? values.tob.format('HH:mm') : '12:00');
        payload = {
          ...payload,
          name: values.name,
          gender: values.gender,
          date_of_birth: dobStr,
          time_of_birth: tobStr,
          timezone: values.timezone
        };
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Autentikasi gagal');
      }

      const data = await res.json();
      if (data.token) {
        const cleanEmail = String(data.user?.email || values.email).toLowerCase().trim();
        onAuthSuccess(data.token, cleanEmail, mode === 'register');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Koneksi ke server gagal.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAttempt = async (values: any) => {
    if (mode === 'register' && !agreed) {
      setDisclaimerModalOpen(true);
      return;
    }
    handleSubmit(values);
  };

  const acceptDisclaimer = async (values: any) => {
    setAgreed(true);
    await handleSubmit(values);
    setDisclaimerModalOpen(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', padding: '16px' }}>
      <Card style={{ 
        border: '3px solid #1a202c', 
        borderRadius: '16px', 
        boxShadow: '6px 6px 0 #1a202c',
        width: '100%',
        maxWidth: '450px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Title level={3} style={{ margin: 0, fontWeight: 900 }}><ProjectTwoTone style={{ marginRight: 8 }} /> Portal Akun BaZi</Title>
          <Paragraph style={{ margin: '4px 0 0 0', color: '#495057', fontWeight: 650 }}>
            Masuk atau daftar untuk mengakses personal AI coaching.
          </Paragraph>
          <Radio.Group 
            value={mode} 
            onChange={e => {
              setMode(e.target.value);
              setErrorMsg('');
              // Reset form fields when switching mode to avoid stale values
              form.resetFields();
              setAgreed(false);
            }} 
            style={{ marginTop: '16px' }}
          >
            <Radio.Button value="login" style={{ fontWeight: 700 }}>Masuk (Login)</Radio.Button>
            <Radio.Button value="register" style={{ fontWeight: 700 }}>Daftar (Register)</Radio.Button>
          </Radio.Group>
        </div>

        {errorMsg && (
          <Alert message={errorMsg} type="error" showIcon style={{ marginBottom: '16px', border: '2px solid #e03131', borderRadius: '8px' }} />
        )}

        <Form 
          form={form}
          layout="vertical" 
          initialValues={{ email: '', password: '', name: '', timezone: 'Asia/Jakarta' }}
          onFinish={handleRegisterAttempt}
        >
          <Form.Item label={<span style={{ fontWeight: 800 }}>Email Address</span>} name="email" rules={[{ required: true, type: 'email' }]}>
            <Input prefix={<UserOutlined />} placeholder="nama@email.com" style={{ border: '2px solid #1a202c', borderRadius: '8px' }} />
          </Form.Item>

          <Form.Item label={<span style={{ fontWeight: 800 }}>Password</span>} name="password" rules={[{ required: true }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="••••••••" style={{ border: '2px solid #1a202c', borderRadius: '8px' }} />
          </Form.Item>              {mode === 'register' && (
            <>
              <Form.Item label={<span style={{ fontWeight: 800 }}>Nama Lengkap</span>} name="name" rules={[
                { required: true, message: 'Masukkan nama lengkap kamu' },
                { min: 2, message: 'Nama minimal 2 karakter' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value && getFieldValue('password') && value === getFieldValue('password')) {
                      return Promise.reject(new Error('Nama tidak boleh sama dengan password'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}>
                <Input prefix={<SmileOutlined />} placeholder="Contoh: John Doe" style={{ border: '2px solid #1a202c', borderRadius: '8px' }} />
              </Form.Item>

              <Form.Item label={<span style={{ fontWeight: 800 }}>Gender</span>} name="gender" rules={[{ required: true, message: 'Pilih gender kamu' }]}>
                <Radio.Group style={{ display: 'flex', gap: '8px', width: '100%' }}>
                  <Radio.Button value="male" style={{ flex: 1, textAlign: 'center', fontWeight: 700 }}>Pria</Radio.Button>
                  <Radio.Button value="female" style={{ flex: 1, textAlign: 'center', fontWeight: 700 }}>Wanita</Radio.Button>
                </Radio.Group>
              </Form.Item>

              <Form.Item label={<span style={{ fontWeight: 800 }}>Tanggal Lahir</span>} name="dob" rules={[{ required: true, message: 'Pilih tanggal lahir kamu' }]}>
                <DatePicker style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }} placeholder="Pilih Tanggal Lahir" />
              </Form.Item>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', border: '2px solid #1a202c', padding: '8px 12px', borderRadius: '8px', background: '#fff' }}>
                <span style={{ fontWeight: 800, fontSize: '0.85rem' }}>Jam Lahir Tidak Diketahui?</span>
                <Switch checked={unknownTob} onChange={(checked) => setUnknownTob(checked)} style={{ background: unknownTob ? '#fab005' : '#ccc' }} />
              </div>

              {!unknownTob && (
                <Form.Item label={<span style={{ fontWeight: 800 }}>Jam Lahir</span>} name="tob" rules={[{ required: !unknownTob, message: 'Pilih jam lahir kamu' }]}>
                  <TimePicker format="HH:mm" style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }} placeholder="Pilih Jam Lahir" />
                </Form.Item>
              )}

              <Form.Item label={<span style={{ fontWeight: 800 }}>Zona Waktu</span>} name="timezone" rules={[{ required: true, message: 'Pilih zona waktu kamu' }]}>
                <Select style={{ width: '100%' }} placeholder="Pilih Zona Waktu">
                  <Option value="Asia/Jakarta">WIB (Jakarta / UTC+7)</Option>
                  <Option value="Asia/Makassar">WITA (Bali / UTC+8)</Option>
                  <Option value="Asia/Jayapura">WIT (Jayapura / UTC+9)</Option>
                </Select>
              </Form.Item>
            </>
          )}

          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            icon={<LoginOutlined />}
            style={{
              height: '42px',
              background: '#fab005',
              borderColor: '#1a202c',
              color: '#1a202c',
              fontWeight: 900,
              fontSize: '0.95rem',
              boxShadow: '2px 2px 0 #1a202c',
              borderRadius: '8px',
              marginTop: '12px'
            }}
          >
            {mode === 'login' ? 'Masuk ke Akun' : 'Daftar Akun Baru'}
          </Button>
        </Form>

        {/* Disclaimer Modal for Registration */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <SafetyCertificateTwoTone twoToneColor="#faad14" style={{ fontSize: '22px' }} />
              <span style={{ fontWeight: 900, fontSize: '16px' }}>Disclaimer & Ketentuan</span>
            </div>
          }
          open={disclaimerModalOpen}
          onCancel={() => {
            setDisclaimerModalOpen(false);
          }}
          footer={[
            <Button
              key="cancel"
              icon={<CloseCircleOutlined />}
              onClick={() => {
                setDisclaimerModalOpen(false);
                setAgreed(false);
              }}
              style={{
                border: '2px solid #1a202c',
                borderRadius: '8px',
                fontWeight: 800,
                height: '42px'
              }}
            >
              Tolak & Batalkan
            </Button>,
            <Button
              key="agree"
              type="primary"
              onClick={() => {
                const values = form.getFieldsValue();
                acceptDisclaimer(values);
              }}
              loading={loading}
              style={{
                background: '#2b8a3e',
                border: '2px solid #1a202c',
                color: '#fff',
                fontWeight: 900,
                borderRadius: '8px',
                boxShadow: '2px 2px 0 #1a202c',
                height: '42px'
              }}
              icon={<SafetyCertificateTwoTone twoToneColor="#52c41a" />}
            >
              Saya Setuju & Lanjutkan
            </Button>
          ]}
          width={520}
        >
          <div style={{ padding: '8px 0' }}>
            <Alert
              message={<span style={{ fontWeight: 800 }}>PENTING — Baca Dengan Seksama</span>}
              description="Dengan menyetujui disclaimer ini, Anda mengkonfirmasi bahwa Anda telah membaca dan memahami seluruh ketentuan di bawah ini sebelum melanjutkan pendaftaran."
              type="warning"
              showIcon
              style={{ border: '2px solid #f59f00', borderRadius: '8px', marginBottom: '16px' }}
            />

            <div style={{
              background: '#fdf6ee',
              border: '2px dashed #b08968',
              borderRadius: '10px',
              padding: '16px',
              fontSize: '0.8rem',
              lineHeight: 1.7,
              color: '#7a5a2a',
              fontWeight: 600,
              maxHeight: '350px',
              overflowY: 'auto',
              whiteSpace: 'pre-line'
            }}>
              <p style={{ fontWeight: 800, marginBottom: '8px', color: '#9a7a4a' }}>
                <SafetyCertificateTwoTone twoToneColor="#b08968" style={{ marginRight: 6 }} /> DISCLAIMER
              </p>
              <p style={{ margin: 0 }}>
                {`Seluruh hasil yang disajikan merupakan interpretasi berdasarkan perhitungan sistematis menggunakan metode BaZi (Empat Pilar Kehidupan), kalender Tiongkok kuno, serta formula dan algoritma yang telah ditetapkan dalam literatur klasik dan modern.

Menurut filosofi Tiongkok Kuno, baik dan buruknya perjalanan hidup seseorang dipengaruhi oleh tiga faktor utama yang dikenal sebagai Tian (Langit), Di (Bumi), dan Ren (Manusia):

1. Langit (Tian) — Kehendak Sang Pencipta dan waktu kelahiran.
2. Bumi (Di) — Lingkungan, tempat, dan kondisi alam semesta.
3. Manusia (Ren) — Pilihan, tindakan, karakter, dan usaha diri sendiri.

Ketiga faktor tersebut bekerja secara dinamis dan saling memengaruhi dalam prinsip keseimbangan. Oleh karena itu, seseorang yang memiliki indikasi kurang baik dalam perhitungan belum tentu mengalami hal buruk, karena dapat diimbangi oleh lingkungan yang mendukung dan tindakan yang tepat. Sebaliknya, seseorang yang memiliki indikasi baik dalam perhitungan belum tentu selalu memperoleh hasil yang baik apabila faktor lingkungan dan tindakan pribadinya tidak mendukung.

Hasil analisis ini bertujuan sebagai sarana refleksi, pengembangan diri, dan bahan pertimbangan dalam mengambil keputusan. Pengguna tetap bertanggung jawab penuh atas setiap keputusan dan tindakan yang diambil berdasarkan informasi yang tersedia.

Catatan Penting: Sistem ini menggunakan metode perhitungan berbasis data kelahiran, kalender astronomi, dan rumus yang terdokumentasi dalam ilmu BaZi.
Kami tidak bertanggung jawab apabila ada kesalahan dalam input dan output tanggal lahir.`}
              </p>
            </div>

            <div style={{ marginTop: '16px', padding: '12px', background: '#fff', border: '1px solid #dee2e6', borderRadius: '8px', textAlign: 'center' }}>
              <span style={{ fontWeight: 800, color: '#1a202c', fontSize: '0.9rem' }}>
                Dengan klik <strong>"Saya Setuju & Lanjutkan"</strong>, Anda menyetujui seluruh ketentuan di atas.
              </span>
            </div>
          </div>
        </Modal>
      </Card>
    </div>
  );
};
export default AuthPage;
